/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */
import { useHistory } from 'react-router-dom';
import { Button, Progress } from 'reactstrap';
import { ROUTES } from '../../../../constants/routes';
import { generateUrl } from '../../../../routes/util';
import { InitiativeBenchmarking } from '../../../../types/initiative';
import { naturalSort } from '../../../../utils';
import { BasicAlert } from '../../../alert/BasicAlert';
import SimpleTooltip from '../../../simple-tooltip';
import Table, { TableColumn, TableVariant } from '../../../table/Table';
import {
  getAnswered,
  getEffectiveDate,
  getPercentNa,
  getPercentNr,
  getPercentPrivate,
  getProgress,
  getTotalQuestions,
  getUnanswered,
} from './utils';
import { QUESTION } from '../../../../constants/terminology';

interface LatestSurveyTableProps {
  portfolioId: string;
  companies: InitiativeBenchmarking[];
  hasFilters: boolean;
}

const LatestSurveyTable = ({ portfolioId, companies, hasFilters }: LatestSurveyTableProps) => {
  const history = useHistory();

  const handleClickCompany = (initiativeId: string) => {
    history.push(generateUrl(ROUTES.PORTFOLIO_TRACKER_COMPANY, { portfolioId: portfolioId, companyId: initiativeId }));
  }

  const tableColumns: TableColumn<InitiativeBenchmarking>[] = [
    {
      accessor: 'name',
      Header: 'Company name',
      Cell: (c) => {
        const company = c.row.original;
        return <Button color='link' className='text-left' onClick={() => handleClickCompany(company._id)}>{company.name}</Button>;
      },
      sortType: (a, b) => naturalSort(a.original.name, b.original.name),
      sticky: 'left',
    },
    {
      accessor: 'surveys',
      Header: 'Surveys',
      cellProps: {
        className: 'text-center',
      },
      Cell: (c) => getEffectiveDate(c.row.original).display,
      sortType: (a, b) =>
        naturalSort(String(getEffectiveDate(a.original).sort), String(getEffectiveDate(b.original).sort)),
    },
    {
      accessor: 'latestSurvey',
      Header: 'Latest survey',
      cellProps: {
        className: 'text-center',
      },
      Cell: (c) => {
        const progress = getProgress(c.row.original);
        if (!c.row.original.latestSurvey) {
          return '-';
        }
        return (
          <SimpleTooltip text={progress.sort}>
            <Progress value={progress.percent} />
          </SimpleTooltip>
        );
      },
      sortType: (a, b) => naturalSort(String(getProgress(a.original).sort), String(getProgress(b.original).sort)),
    },
    {
      accessor: 'questionsInScope',
      Header: `${QUESTION.CAPITALIZED_PLURAL} in scope`,
      cellProps: {
        className: 'text-center',
      },
      Cell: (c) => getTotalQuestions(c.row.original),
      sortType: (a, b) => naturalSort(String(getTotalQuestions(a.original)), String(getTotalQuestions(b.original))),
    },
    {
      accessor: 'unanswered',
      Header: 'Unanswered',
      cellProps: {
        className: 'text-center',
      },
      Cell: (c) => getUnanswered(c.row.original),
      sortType: (a, b) => naturalSort(String(getUnanswered(a.original)), String(getUnanswered(b.original))),
    },
    {
      accessor: 'answered',
      Header: 'Answered',
      cellProps: {
        className: 'text-center',
      },
      Cell: (c) => getAnswered(c.row.original),
      sortType: (a, b) => naturalSort(String(getAnswered(a.original)), String(getAnswered(b.original))),
    },
    {
      accessor: 'private',
      Header: '% Private',
      cellProps: {
        className: 'text-center',
      },
      Cell: (c) => getPercentPrivate(c.row.original).display,
      sortType: (a, b) => naturalSort(getPercentPrivate(a.original).sort, getPercentPrivate(b.original).sort),
    },
    {
      accessor: 'na',
      Header: '% N/A',
      cellProps: {
        className: 'text-center',
      },
      Cell: (c) => getPercentNa(c.row.original).display,
      sortType: (a, b) => naturalSort(getPercentNa(a.original).sort, getPercentNa(b.original).sort),
    },
    {
      accessor: 'nr',
      Header: '% N/R',
      cellProps: {
        className: 'text-center',
      },
      Cell: (c) => getPercentNr(c.row.original).display,
      sortType: (a, b) => naturalSort(getPercentNr(a.original).sort, getPercentNr(b.original).sort),
    },
  ];

  return (
    <div className='latest-survey-table'>
      <div className='table-wrapper'>
        <Table columns={tableColumns} data={companies} pageSize={20} variant={TableVariant.StickyColumns} />
        {companies?.length === 0 ? (
          <BasicAlert type='secondary' className='mt-2'>
            {hasFilters ? 'No company match your criteria' : 'You have no companies assigned to this portfolio'}
          </BasicAlert>
        ) : null}
      </div>
    </div>
  );
};

export default LatestSurveyTable;
